import { usePrefersReducedMotion } from './motion';

interface SkeletonProps {
  /** CSS width — e.g. '60%', '120px'. Omit for a block that fills its parent. */
  w?: string;
  h?: string | number;
  /** Circle instead of bar (avatars, dots). */
  round?: boolean;
  className?: string;
}

/**
 * Skeleton — shimmer bar shown while a panel loads.
 *
 * Pear's panels rendered nothing until data landed, which is the single most
 * "web page" tell in the app. Every async surface (thread history, questions,
 * requests, AI toggles) gets one of these instead.
 *
 * Reduced motion: the sweep stops but the placeholder still renders, so the
 * layout does not jump when content arrives.
 */
export function Skeleton({ w, h = 12, round = false, className }: SkeletonProps) {
  const reduced = usePrefersReducedMotion();
  return (
    <div
      data-skeleton="true"
      className={className}
      style={{
        width: w ?? '100%',
        height: h,
        borderRadius: round ? '50%' : 'var(--radius-md, 8px)',
        background: 'var(--pear-field-soft, rgba(246,239,227,.08))',
        animation: reduced ? 'none' : 'pear-shimmer 1.4s linear infinite',
        backgroundImage: reduced
          ? undefined
          : 'linear-gradient(90deg, transparent 0%, rgba(246,239,227,.14) 50%, transparent 100%)',
        backgroundSize: '200px 100%',
        backgroundRepeat: 'no-repeat',
      }}
      aria-hidden="true"
    />
  );
}

/** N stacked bars — the common "something is loading" block. */
export function SkeletonBlock({ lines = 3 }: { lines?: number }) {
  const widths = ['60%', '80%', '40%', '70%', '55%'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)', width: '100%' }}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} w={widths[i % widths.length]} />
      ))}
    </div>
  );
}
